import React, { useEffect, useState } from 'react';
import { motion } from 'motion/react';
import SEO from '@/components/SEO';
import { BackgroundAnimation } from '@/components/background-animation';
import { VISUAL_PRESETS } from '@/data/presets';
import { Play, Sparkles, Share2, Heart, Eye, ArrowRight, BookOpen, FileText } from 'lucide-react';
import { Link } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { buttonVariants } from '@/components/ui/button';
import { apiFetch } from '@/lib/api';
import { Skeleton } from '@/components/ui/skeleton';
import { DiscordCTA } from '@/components/DiscordCTA';
import InternalLinksSection from '@/components/InternalLinksSection';

interface GifItem {
  id: string;
  type: string;
  url: string;
  title?: string;
  key?: string;
}

const getYouTubeEmbedUrl = (url: string, autoplay = false) => {
  const match = url.match(/(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/(?:[^\/\n\s]+\/\S+\/|(?:v|e(?:mbed)?)\/|\S*?[?&]v=|shorts\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
  const videoId = match?.[1];
  if (!videoId) return url;
  
  const params = new URLSearchParams({
    autoplay: autoplay ? '1' : '0',
    mute: '1',
    loop: '1',
    playlist: videoId,
    controls: '0',
    modestbranding: '1',
    rel: '0',
    iv_load_policy: '3',
    fs: '0',
    disablekb: '1',
    playsinline: '1',
    widget_referrer: window.location.origin
  });
  
  return `https://www.youtube.com/embed/${videoId}?${params.toString()}`;
};

const getInstagramEmbedUrl = (url: string) => {
  if (!url) return url;
  let cleanUrl = url.split('?')[0];
  if (!cleanUrl.startsWith('http')) {
    cleanUrl = 'https://www.instagram.com/' + cleanUrl.replace(/^(www\.)?instagram\.com\//, '');
  }
  // Standard embed doesn't support autoplay, we just clip it heavily
  return `${cleanUrl.endsWith('/') ? cleanUrl : cleanUrl + '/'}embed/`;
};

import { toast } from 'sonner';

const ShowcaseItem = ({ item, idx }: { item: GifItem; idx: number }) => {
  const [liked, setLiked] = useState(false);
  const [isInView, setIsInView] = useState(false);
  const containerRef = React.useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsInView(entry.isIntersecting);
      },
      { threshold: 0.1 }
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const handleShare = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    navigator.clipboard.writeText(item.url);
    toast.success("Link copied to clipboard!");
  };

  const toggleLike = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setLiked(!liked);
    if (!liked) {
      toast.success("Added to favorites!");
    }
  };

  return (
    <motion.div
      ref={containerRef}
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ delay: idx * 0.1 }}
      className="group relative rounded-[2rem] overflow-hidden border border-zinc-950/10 bg-black hover:shadow-2xl transition-all duration-500"
    >
      <div className="aspect-[9/16] bg-zinc-900 flex items-center justify-center relative overflow-hidden">
        {item.type === 'YOUTUBE' ? (
          isInView ? (
            <iframe
              src={getYouTubeEmbedUrl(item.url, true)}
              className="w-full h-full border-0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          ) : (
             <div className="w-full h-full bg-zinc-950 flex items-center justify-center">
                <Play className="w-12 h-12 text-white/20" />
             </div>
          )
        ) : item.type === 'INSTAGRAM' ? (
          <div className="absolute inset-0 flex items-center justify-center overflow-hidden bg-white">
            <iframe
              src={getInstagramEmbedUrl(item.url)}
              className="w-full h-full border-0"
              allowTransparency
              frameBorder="0"
              scrolling="no"
            />
          </div>
        ) : (
          item.url.toLowerCase().endsWith('.mp4') || item.url.toLowerCase().endsWith('.webm') || item.key?.toLowerCase().endsWith('.mp4') ? (
            <video 
              src={item.url} 
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 pointer-events-none"
              autoPlay={isInView}
              muted 
              loop 
              playsInline
            />
          ) : (
            <img 
              src={item.url} 
              alt={item.title || `Showcase ${idx}`} 
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 pointer-events-none" 
              loading="lazy"
            />
          )
        )}

        <div className="absolute top-6 right-6 flex flex-col gap-3 opacity-0 group-hover:opacity-100 transition-opacity translate-x-4 group-hover:translate-x-0 transition-all z-20">
          <button 
            className={cn(
              "w-10 h-10 rounded-xl backdrop-blur-md border border-white/20 flex items-center justify-center transition-all",
              liked ? "bg-red-500 text-white border-red-400" : "bg-white/20 text-white hover:bg-white/40"
            )}
            onClick={toggleLike}
          >
            <Heart className={cn("w-4 h-4", liked && "fill-current")} />
          </button>
          <button 
            className="w-10 h-10 rounded-xl bg-white/20 backdrop-blur-md border border-white/20 flex items-center justify-center text-white hover:bg-cyan-600 transition-colors"
            onClick={handleShare}
          >
            <Share2 className="w-4 h-4" />
          </button>
        </div>
      </div>
    </motion.div>
  );
};

const ShowcasePage: React.FC = () => {
  const [items, setItems] = useState<GifItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'all' | 'sim' | 'youtube' | 'instagram'>('all');

  useEffect(() => {
    const fetchItems = async () => {
      try {
        const response = await apiFetch('/api/showcase-gifs');
        if (response.ok) {
          const data = await response.json();
          if (data && data.gifs) {
            setItems(data.gifs);
          }
        }
      } catch (err) {
        console.error('Failed to fetch showcase items:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchItems();
  }, []);

  // Use a subset of presets as fallback if no real items
  const featuredCreations = VISUAL_PRESETS.filter(p => ['Vibrant', 'Metallic', 'Glass', 'Tech', 'Space'].includes(p.category));

  const filteredItems = items.filter(item => {
    if (activeTab === 'all') return true;
    if (activeTab === 'sim') return item.type === 'FILE';
    if (activeTab === 'youtube') return item.type === 'YOUTUBE';
    if (activeTab === 'instagram') return item.type === 'INSTAGRAM';
    return true;
  });

  const showPresets = items.length === 0 && activeTab === 'all';

  return (
    <div className="flex flex-col min-h-screen">
      <SEO 
        title="Showcase | Featured Simulations"
        description="Explore the best ball simulations created with Fun Circle Games. Hypnotic loops, satisfying physics, and viral-ready animations."
        keywords="physics showcase, ball simulation gallery, satisfying videos, asmr gallery, creator studio examples"
        canonical="/showcase"
      />
      <BackgroundAnimation />

      <section className="pt-32 pb-20 px-6">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-20"
          >
             <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-cyan-50 border border-cyan-100 mb-8 font-sans text-[10px] uppercase tracking-[0.3em] text-cyan-600 mx-auto">
              <Sparkles className="w-3.5 h-3.5" />
              <span>Community Highlights</span>
            </div>
            <h1 className="text-4xl sm:text-6xl md:text-8xl font-bold font-display text-zinc-950 mb-8 uppercase tracking-tighter leading-none">
              Simulation <span className="text-zinc-400">Showcase</span>
            </h1>
            <p className="text-zinc-500 text-lg max-w-2xl mx-auto font-sans">
              Discover what's possible with our professional physics engine. These featured creations demonstrate the power of high-fidelity simulation and visual styling.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {loading ? (
              [1, 2, 3, 4, 5, 6].map((i) => (
                <Skeleton key={i} className="aspect-[9/16] rounded-[2rem] bg-zinc-100" />
              ))
            ) : filteredItems.length > 0 ? (
              filteredItems.map((item, idx) => (
                <ShowcaseItem key={item.id} item={item} idx={idx} />
              ))
            ) : showPresets ? (
              featuredCreations.map((creation, idx) => (
                <motion.div
                  key={creation.name}
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: idx * 0.1 }}
                  className="group relative rounded-[2rem] overflow-hidden border border-zinc-950/10 bg-black hover:shadow-2xl transition-all duration-500"
                >
                  <div className="aspect-[9/16] bg-zinc-900 flex items-center justify-center relative overflow-hidden">
                    <div className="absolute inset-0 bg-gradient-to-br from-zinc-800 to-zinc-950 opacity-50 group-hover:scale-110 transition-transform duration-700" />
                    
                    <div className="relative z-10 text-center">
                       <div className="w-16 h-16 rounded-full bg-white/10 backdrop-blur-md border border-white/20 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform cursor-pointer">
                          <Play className="w-6 h-6 text-white fill-white" />
                       </div>
                       <span className="text-white/40 text-[10px] font-bold uppercase tracking-widest">Preview</span>
                    </div>

                    <div className="absolute top-6 right-6 flex flex-col gap-3 opacity-0 group-hover:opacity-100 transition-opacity translate-x-4 group-hover:translate-x-0 transition-all">
                      <button 
                        className="w-10 h-10 rounded-xl bg-white/20 backdrop-blur-md border border-white/20 flex items-center justify-center text-white hover:bg-white/40 transition-colors"
                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
                      >
                        <Heart className="w-4 h-4" />
                      </button>
                      <button 
                        className="w-10 h-10 rounded-xl bg-white/20 backdrop-blur-md border border-white/20 flex items-center justify-center text-white hover:bg-cyan-600 transition-colors"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          navigator.clipboard.writeText(`${window.location.origin}/studio?preset=${encodeURIComponent(creation.name)}`);
                        }}
                      >
                        <Share2 className="w-4 h-4" />
                      </button>
                    </div>

                    <div className="absolute bottom-0 left-0 right-0 p-8 bg-gradient-to-t from-black/80 to-transparent text-white">
                      <Link 
                        to={`/studio?preset=${encodeURIComponent(creation.name)}`}
                        className={cn(buttonVariants({ variant: "outline", size: "sm" }), "w-full rounded-xl border-white/20 hover:bg-white hover:text-black text-[10px] uppercase tracking-widest h-10 pointer-events-auto")}
                      >
                        Remix Setup
                      </Link>
                    </div>
                  </div>
                </motion.div>
              ))
            ) : (

              <div className="col-span-full py-20 text-center">
                <p className="text-zinc-400 font-sans italic">No items found in this category.</p>
              </div>
            )}
          </div>

          <div className="mt-20 text-center space-y-24">
            <div>
              <Link to="/register" className={cn(buttonVariants({ size: "lg" }), "rounded-full px-12 h-16 bg-cyan-600 text-white hover:bg-cyan-700 transition-all font-bold uppercase tracking-[0.3em] text-xs font-sans shadow-lg shadow-cyan-600/10")}>
                Create Your Own <ArrowRight className="ml-3 w-5 h-5" />
              </Link>
            </div>

            <div className="pt-16 border-t border-zinc-100 text-left space-y-6 max-w-4xl mx-auto">
              <h3 className="text-lg font-bold font-display uppercase tracking-tight text-zinc-950">Next Steps</h3>
              <p className="text-zinc-500 text-sm font-medium font-sans">
                Inspired by the community showcase? Check out these pages to start building:
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                <Link to="/plans" className="p-6 rounded-3xl bg-cyan-50/20 border border-cyan-600/5 hover:border-cyan-600/20 transition-all flex flex-col justify-between group hover:shadow-lg hover:shadow-cyan-600/5">
                  <div className="space-y-4">
                    <div className="w-10 h-10 rounded-2xl bg-cyan-600/10 text-cyan-600 flex items-center justify-center group-hover:scale-110 transition-transform">
                      <Sparkles className="w-5 h-5" />
                    </div>
                    <h4 className="font-bold text-cyan-950 uppercase tracking-tight">Studio Pricing</h4>
                    <p className="text-xs text-zinc-500 font-medium">Unlock full 4K downloads & advanced MIDI audio engine layers.</p>
                  </div>
                  <div className="mt-6 text-[10px] font-black uppercase tracking-widest text-cyan-600 flex items-center gap-1">
                    Explore Plans <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
                  </div>
                </Link>

                <Link to="/tutorials" className="p-6 rounded-3xl bg-cyan-50/20 border border-cyan-600/5 hover:border-cyan-600/20 transition-all flex flex-col justify-between group hover:shadow-lg hover:shadow-cyan-600/5">
                  <div className="space-y-4">
                    <div className="w-10 h-10 rounded-2xl bg-cyan-600/10 text-cyan-600 flex items-center justify-center group-hover:scale-110 transition-transform">
                      <BookOpen className="w-5 h-5" />
                    </div>
                    <h4 className="font-bold text-cyan-950 uppercase tracking-tight">Creator Academy</h4>
                    <p className="text-xs text-zinc-500 font-medium">Learn the exact parameters and audio tips for virality.</p>
                  </div>
                  <div className="mt-6 text-[10px] font-black uppercase tracking-widest text-cyan-600 flex items-center gap-1">
                    Start Learning <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
                  </div>
                </Link>

                <Link to="/case-studies" className="p-6 rounded-3xl bg-cyan-50/20 border border-cyan-600/5 hover:border-cyan-600/20 transition-all flex flex-col justify-between group hover:shadow-lg hover:shadow-cyan-600/5">
                  <div className="space-y-4">
                    <div className="w-10 h-10 rounded-2xl bg-cyan-600/10 text-cyan-600 flex items-center justify-center group-hover:scale-110 transition-transform">
                      <FileText className="w-5 h-5" />
                    </div>
                    <h4 className="font-bold text-cyan-950 uppercase tracking-tight">Case Studies</h4>
                    <p className="text-xs text-zinc-500 font-medium">See how other creators grew their channels using our engine.</p>
                  </div>
                  <div className="mt-6 text-[10px] font-black uppercase tracking-widest text-cyan-600 flex items-center gap-1">
                    Read Studies <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
                  </div>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <DiscordCTA variant="banner" />

      <InternalLinksSection />
    </div>
  );
};

export default ShowcasePage;
